import { useState, ButtonHTMLAttributes } from 'react';
import Popover from './Popover.tsx';
import { OpenBook } from './Icons.tsx';
import { TypoBody, TypoCallout, TypoCaption } from './Typography.tsx';

interface ScoringGuidelineProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  title?: string;
  guideType?: keyof typeof GuideType;
}

const ScoringGuideline = ({ title = 'Scoring System', guideType, ...props }: ScoringGuidelineProps) => {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();

  return (
    <>
      <button
        onClick={(event) => {
          setOpen(true);
          setAnchor(event.currentTarget);
        }}
        className={`
        flex items-center justify-center px-6 py-4 gap-2.5 rounded-lg flex-shrink-0
        ${open ? 'bg-shade-10' : 'hover:bg-shade-5'}
        `}
        {...props}
      >
        <OpenBook className="text-primary-icon w-5 h-5" />
        <span className="text-callout font-medium text-neutral-text">{title}</span>
      </button>
      {guideType ? (
        <ScoringGuidelineDialog
          guideType={guideType}
          open={open}
          anchor={anchor}
          onClose={() => setOpen(false)}
        />
      ) : (
        <FullGuidelineDialog
          open={open}
          anchor={anchor}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

interface DialogProps {
  guideType?: keyof typeof GuideType;
  open: boolean;
  onClose: () => void;
  anchor?: Element | null;
}

const FullGuidelineDialog = ({ open, anchor, onClose }: DialogProps) => (
  <Popover
    open={open}
    title="About Study Center"
    anchor={anchor}
    className="mt-2.5"
    placement="bottom-end"
    onClose={onClose}
    width="fit-content"
  >
    <TypoBody>
      The SATPrac STUDY CENTER is a key organizational tool that you can use to project your FUNDEMANTALS FLUENCY.
      If you know what you know you need to know on game day, you will win...
      but you need to put in the time to get yourself to that point!
      <br /><br />
      The study center provides you with a projected FUNDAMENTALS SAT SCORE on the RW and MATH that is determined
      in response to a self-assessment of how well you have done on the video tutorials,
      question bank tutorials, and SATPrac packs associated with each of the 43 question types.
      <br /><br />
      To get this FUNDAMENTALS SCORE, for each of the 43 question types,
      you will rate yourself on the video tutorials associated with that question type on a scale of 0 to 5
    </TypoBody>

    <ul className="mt-6">
      {GUIDE_LIST[GuideType.VIDEO].map((guide, index) => (
        <li key={index} className="flex items-center gap-4 mb-1.5">
          <div className="w-6 h-6 flex items-center justify-center bg-neutral-hover rounded-full">
            <TypoCaption className="text-neutral-text-strong font-semibold leading-none">{index}</TypoCaption>
          </div>
          <TypoCallout>{guide}</TypoCallout>
        </li>
      ))}
    </ul>

    <TypoBody className="mt-6">You will also rate yourself on the SATPrac PACKS on a scale of 0 to 5</TypoBody>

    <ul className="mt-6">
      {GUIDE_LIST[GuideType.READING].map((guide, index) => (
        <li key={index} className="flex items-center gap-4 mb-1.5">
          <div className="w-6 h-6 flex items-center justify-center bg-neutral-hover rounded-full">
            <TypoCaption className="text-neutral-text-strong font-semibold leading-none">{index}</TypoCaption>
          </div>
          <TypoCallout>{guide}</TypoCallout>
        </li>
      ))}
    </ul>
  </Popover>
);

const ScoringGuidelineDialog = ({ open, anchor, guideType = GuideType.READING, onClose }: DialogProps) => (
  <Popover
    open={open}
    title="Scoring System Guidelines"
    anchor={anchor}
    className="mt-2.5"
    placement="top-end"
    onClose={onClose}
    width="fit-content"
  >
    <ul className="mt-6">
      {GUIDE_LIST[guideType].map((guide, index) => (
        <li key={index} className="flex items-center gap-4 mb-1.5">
          <div className="w-6 h-6 flex items-center justify-center bg-neutral-hover rounded-full">
            <TypoCaption className="text-neutral-text-strong font-semibold leading-none">{index}</TypoCaption>
          </div>
          <TypoCallout>{guide}</TypoCallout>
        </li>
      ))}
    </ul>
  </Popover>
);

enum GuideType {
  READING = 'READING',
  VIDEO = 'VIDEO',
}

const GUIDE_LIST = {
  [GuideType.READING]: [
    'Haven’t done any of the SATPrac pack questions.',
    'Did 25% of the SATPrac pack questions.',
    'Did 50% of the SATPrac pack questions',
    'Did 100% - missed more than I would like.',
    'Did 100% - only missed a couple of the questions.',
    'Did 100% - I dominated the SATPrac pack questions.'
  ],
  [GuideType.VIDEO]: [
    'Haven’t watch the tutorial.',
    'Watched some of the tutorial.',
    'Watched did not pause to do the questions.',
    'I missed more questions than I would like.',
    'I only missed a couple of the questions.',
    'I dominated the questions in this video.'
  ],
};

export default ScoringGuideline;
